import { EnvConfig } from '@gripp/shared-logic';
import { useVideoPlayer, VideoSize, VideoView } from 'expo-video';
import { useEffect, useMemo, useState } from 'react';
import { LayoutChangeEvent, Platform, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { useVideoPlaybackUrl } from './useVideoPlaybackUrl';

type VideoPlayerPanelProps = {
  mediaId: string;
  /** Start playing on mount; matches the web files panel. */
  autoPlay?: boolean;
};

type Box = { width: number; height: number };

/**
 * Largest box with the clip's proportions that fits the space available.
 *
 * Sizing the player to the clip rather than letting it fill and letterbox
 * matters because the surround is not ours to paint: media3's PlayerView draws
 * its own black, so a landscape clip on a portrait screen sits in a black slab
 * that no style on this side can recolour. Leave no surround and the question
 * does not arise.
 */
const fitToBox = (stage: Box | null, video: VideoSize | null): Box | null => {
  if (!stage?.width || !stage?.height || !video?.width || !video?.height) {
    return null;
  }
  const scale = Math.min(stage.width / video.width, stage.height / video.height);
  return { width: video.width * scale, height: video.height * scale };
};

/**
 * Player for a dedicated preview screen: it sizes itself to the clip and
 * centres in whatever space it is given.
 *
 * Deliberately not `VideoComponent`, which derives explicit pixel dimensions
 * from a fixed 16:9 ratio so a video sits correctly in a chat bubble — that
 * wastes most of the screen on a square or portrait clip.
 */
export const VideoPlayerPanel = ({
  mediaId,
  autoPlay = true,
}: VideoPlayerPanelProps) => {
  const { t } = useTranslation();
  const apiUrl = `${EnvConfig.apiUrl}/video/${mediaId}`;
  const { playbackUrl, resolveError } = useVideoPlaybackUrl(apiUrl);

  const videoSource = useMemo(
    () => (playbackUrl ? { uri: playbackUrl } : null),
    [playbackUrl]
  );

  const player = useVideoPlayer(videoSource, (instance) => {
    if (autoPlay) {
      instance.play();
    }
  });

  const [videoSize, setVideoSize] = useState<VideoSize | null>(null);
  const [stage, setStage] = useState<Box | null>(null);

  useEffect(() => {
    if (!player) return;
    setVideoSize(player.videoTrack?.size ?? null);
    const subscription = player.addListener(
      'videoTrackChange',
      ({ videoTrack }) => setVideoSize(videoTrack?.size ?? null)
    );
    return () => subscription.remove();
  }, [player]);

  const onStageLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setStage((current) =>
      current?.width === width && current?.height === height
        ? current
        : { width, height }
    );
  };

  const videoBox = fitToBox(stage, videoSize);

  if (resolveError) {
    return (
      <View style={styles.center}>
        <Text style={styles.message}>{t('asset.files.videoUnavailable')}</Text>
      </View>
    );
  }

  if (!playbackUrl) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={Colors.primary} size="large" />
        <Text style={styles.message}>{t('asset.files.videoLoading')}</Text>
      </View>
    );
  }

  return (
    <View style={styles.stage} onLayout={onStageLayout}>
      <VideoView
        player={player}
        // Until the track reports its size there is nothing to fit to, so fill
        // and let contentFit letterbox for that first moment.
        style={videoBox ?? styles.fill}
        contentFit="contain"
        nativeControls
        allowsFullscreen
        allowsPictureInPicture={Platform.OS !== 'web'}
        // Android only. The default surfaceView composites in its own layer,
        // which misbehaves under views drawn over the player — this screen
        // floats the nav bar across the bottom of the video.
        surfaceType="textureView"
      />
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  message: {
    fontSize: 14,
    color: role.textSecondary,
    textAlign: 'center',
  },
  stage: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fill: {
    width: '100%',
    height: '100%',
  },
}));
